import React, { useContext, useMemo, useState, useCallback } from 'react';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin } from '@/utils/user';
import {
  DivisionSimpleDTO,
  DivisionWithTagsDTO,
  RebateBreakdown,
  RebateRule,
  UnifiedForecastResult,
  RebatePoint,
} from '@erp/shared-types';
import { createDivisionIdToNameMap } from 'src/utils/rebateForecastingUtils';
import paths from 'src/paths';
import useCoreEnums from 'src/api/core/core-enums/useCoreEnums';
import { useRebateFilters } from '../hooks/useRebateFilters';
import { useSplitPoint } from '../hooks/useSplitPoint';
import { useFilteredForecastData } from '../hooks/useFilteredForecastData';
import { useExtendedReportedForecast } from '../hooks/useExtendedReportedForecast';
import { getRebateAmount } from '@/utils/rebateAmountSelector';
import {
  DimensionType,
  TableConfig,
  MultiYearForecastData,
  DimensionGroupingConfig,
  createDefaultCatchAllGroups,
  LineType,
} from '@/types/rebateAnalytics';
import { generateTableName } from '@/utils/rebateAnalyticsTableUtils';
import { StatusTabView } from './StatusTabView';
import { ALL_PREDICTION_ALGORITHMS } from '../ForecastCard';

interface StatusTabProps {
  agreementId: string;
  forecastData?: UnifiedForecastResult;
  forecastLoading: boolean;
  divisions?: DivisionWithTagsDTO[];
  rebateRules?: RebateRule[];
  currentDate?: Date; // Optional current date for date-based calculations (for testing/Storybook)
  ruleTagsMap?: Map<string, string[]>; // Map of ruleId -> tag names
  tagNameToIdMap?: Map<string, string>; // Map of tag name -> tag ID
  isCumulative?: boolean; // Controlled cumulative state from parent
  onCumulativeChange?: (cumulative: boolean) => void; // Callback for cumulative changes
  visibleLines?: Set<string>; // Controlled visible lines from parent
  onVisibleLinesChange?: (lines: Set<string>) => void; // Callback for visible lines changes
  primaryLine?: string; // Controlled primary line from parent
  onPrimaryLineChange?: (line: string) => void; // Callback for primary line changes
  filterFns?: {
    rule?: (bd: RebateBreakdown) => boolean;
    division?: (bd: RebateBreakdown) => boolean;
  };
  activeFilterNames?: string[];
}

/**
 * Find the tag ID for any tag containing "redistribution" (case-insensitive)
 * Matches tags like "Redistribution", "Redistribution Allowance", etc.
 */
function findRedistributionTagId(
  tagNameToIdMap?: Map<string, string>,
): string | null {
  if (!tagNameToIdMap) return null;

  for (const [tagName, tagId] of tagNameToIdMap.entries()) {
    if (tagName.toLowerCase().includes('redistribution')) {
      return tagId;
    }
  }
  return null;
}

/**
 * Create a DimensionGroupingConfig for RULE_TAG with a single "Redistribution" group
 */
function createRedistributionGroupingConfig(
  redistributionTagId: string,
): DimensionGroupingConfig {
  return {
    userGroups: [
      {
        id: 'redistribution-group',
        name: 'Redistribution',
        itemIds: [redistributionTagId],
      },
    ],
    catchAllGroups: createDefaultCatchAllGroups(DimensionType.RULE_TAG),
  };
}

/**
 * Convert date to string (handles both Date objects and strings)
 * Prints the date the way it arrived natively - strings as-is, Date objects as ISO strings
 */
function dateToString(date: Date | string): string {
  if (typeof date === 'string') {
    return date;
  }
  if (date instanceof Date) {
    return date.toISOString();
  }
  return String(date);
}

/**
 * Compare dates for sorting (handles both Date objects and strings)
 */
function compareDates(a: Date | string, b: Date | string): number {
  const dateA = typeof a === 'string' ? new Date(a).getTime() : a.getTime();
  const dateB = typeof b === 'string' ? new Date(b).getTime() : b.getTime();
  return dateA - dateB;
}

/**
 * Calculate cumulative rebate from an array of points
 */
function calculateCumulativeRebate(points: RebatePoint[]): number {
  return points.reduce((sum, point) => sum + point.totalRebateAmount, 0);
}

/**
 * Get date range from an array of points (with zeros included)
 */
function getDateRangeWithZeros(
  points: RebatePoint[],
): { startDate: string; endDate: string } | null {
  if (points.length === 0) return null;
  const sorted = [...points].sort((a, b) => compareDates(a.date, b.date));
  return {
    startDate: dateToString(sorted[0].date),
    endDate: dateToString(sorted[sorted.length - 1].date),
  };
}

/**
 * Get date range from an array of points (only non-zero values)
 */
function getDateRangeWithoutZeros(
  points: RebatePoint[],
): { startDate: string; endDate: string } | null {
  const nonZeroPoints = points.filter(
    (point) =>
      Math.abs(point.totalRebateAmount) > 0.01 ||
      (point.breakdowns && point.breakdowns.length > 0),
  );
  if (nonZeroPoints.length === 0) return null;
  const sorted = [...nonZeroPoints].sort((a, b) =>
    compareDates(a.date, b.date),
  );
  return {
    startDate: dateToString(sorted[0].date),
    endDate: dateToString(sorted[sorted.length - 1].date),
  };
}

/**
 * Calculate total volume purchased from an array of points
 */
function calculateTotalVolume(points: RebatePoint[]): number {
  return points.reduce((sum, point) => sum + (point.totalVolume || 0), 0);
}

/**
 * Log forecast debug tables to console
 */
function logForecastDebugTables(forecastData: UnifiedForecastResult) {
  if (!forecastData) return;

  const estimates = forecastData.estimates || [];
  const linearRegression =
    forecastData.predictions?.['linear-regression'] || [];
  const reported = forecastData.reported || [];

  // Calculate metrics for each type
  const estimateCumulative = calculateCumulativeRebate(estimates);
  const linearRegressionCumulative =
    calculateCumulativeRebate(linearRegression);
  const reportedCumulative = calculateCumulativeRebate(reported);

  const estimateVolume = calculateTotalVolume(estimates);
  const linearRegressionVolume = calculateTotalVolume(linearRegression);
  const reportedVolume = calculateTotalVolume(reported);

  // Get last incremental rebate (or 0 if empty)
  const estimateIncremental =
    estimates.length > 0
      ? estimates[estimates.length - 1].totalRebateAmount
      : 0;
  const linearRegressionIncremental =
    linearRegression.length > 0
      ? linearRegression[linearRegression.length - 1].totalRebateAmount
      : 0;
  const reportedIncremental =
    reported.length > 0 ? reported[reported.length - 1].totalRebateAmount : 0;

  // Build table data
  const tableData = [
    {
      type: 'Estimate',
      withZeros: getDateRangeWithZeros(estimates),
      withoutZeros: getDateRangeWithoutZeros(estimates),
      incrementalRebate: estimateIncremental,
      cumulativeRebate: estimateCumulative,
      volumePurchased: estimateVolume,
    },
    {
      type: 'Linear Regression',
      withZeros: getDateRangeWithZeros(linearRegression),
      withoutZeros: getDateRangeWithoutZeros(linearRegression),
      incrementalRebate: linearRegressionIncremental,
      cumulativeRebate: linearRegressionCumulative,
      volumePurchased: linearRegressionVolume,
    },
    {
      type: 'Reported',
      withZeros: getDateRangeWithZeros(reported),
      withoutZeros: getDateRangeWithoutZeros(reported),
      incrementalRebate: reportedIncremental,
      cumulativeRebate: reportedCumulative,
      volumePurchased: reportedVolume,
    },
  ];

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Format number
  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // // Log table WITH zeros
  // console.log('\n⸻');
  // console.log('✔️ WITH zeros included');
  // console.log('(uses the first and last dates in each array)');
  // console.table(
  //   tableData.map((row) => ({
  //     Type: row.type,
  //     'Start Date': row.withZeros?.startDate || '-',
  //     'End Date': row.withZeros?.endDate || '-',
  //     'Incremental Rebate': formatCurrency(row.incrementalRebate),
  //     'Cumulative Rebate': formatCurrency(row.cumulativeRebate),
  //     'Volume Purchased': formatCurrency(row.volumePurchased),
  //   })),
  // );

  // // Log table WITHOUT zeros
  // console.log('\n⸻');
  // console.log('✔️ WITHOUT zeros included');
  // console.log('(only dates with non-zero totals or breakdowns)');
  // console.table(
  //   tableData.map((row) => ({
  //     Type: row.type,
  //     'Start Date': row.withoutZeros?.startDate || '-',
  //     'End Date': row.withoutZeros?.endDate || '-',
  //     'Incremental Rebate': formatCurrency(row.incrementalRebate),
  //     'Cumulative Rebate': formatCurrency(row.cumulativeRebate),
  //     'Volume Purchased': formatCurrency(row.volumePurchased),
  //   })),
  // );
  // console.log('⸻\n');
}

const StatusTab: React.FC<StatusTabProps> = ({
  agreementId,
  forecastData,
  forecastLoading,
  divisions,
  rebateRules,
  currentDate: currentDateProp,
  ruleTagsMap,
  tagNameToIdMap,
  isCumulative: isCumulativeProp,
  onCumulativeChange: onCumulativeChangeProp,
  visibleLines: visibleLinesProp,
  onVisibleLinesChange: onVisibleLinesChangeProp,
  primaryLine: primaryLineProp,
  onPrimaryLineChange: onPrimaryLineChangeProp,
  filterFns: filterFnsProp,
  activeFilterNames: activeFilterNamesProp,
}) => {
  // Check if user is admin for audit button visibility
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const userIsAdmin = isAdmin(currentUser, viewMode);

  // State for cumulative vs month-by-month toggle (shared between chart and breakdowns)
  // Use controlled props if provided, otherwise use local state
  const [isCumulativeLocal, setIsCumulativeLocal] = useState(true);
  const isCumulative = isCumulativeProp ?? isCumulativeLocal;
  const setIsCumulative = onCumulativeChangeProp ?? setIsCumulativeLocal;

  // State for line visibility and primary selection
  // Use controlled props if provided, otherwise use local state
  // Include all prediction algorithms by default instead of 'predictions'
  const [visibleLinesLocal, setVisibleLinesLocal] = useState<Set<string>>(
    new Set([
      'estimates',
      ...ALL_PREDICTION_ALGORITHMS,
      'reported',
      'previousYear',
    ]),
  );
  const [primaryLineLocal, setPrimaryLineLocal] = useState<LineType>('sarima');
  const visibleLines = visibleLinesProp ?? visibleLinesLocal;
  const setVisibleLines = onVisibleLinesChangeProp ?? setVisibleLinesLocal;
  const primaryLine = primaryLineProp ?? primaryLineLocal;
  const setPrimaryLine = onPrimaryLineChangeProp ?? setPrimaryLineLocal;
  // State for collapsible breakdown tables
  const [ruleBreakdownCollapsed, setRuleBreakdownCollapsed] = useState(false);
  const [divisionBreakdownCollapsed, setDivisionBreakdownCollapsed] =
    useState(false);

  // Note: Prediction algorithm visibility is now controlled via visibleLines
  // Each algorithm (linear-regression, ai-forecasting, etc.) is directly in visibleLines
  // Create division ID to name mapping
  const divisionIdToName = useMemo(() => {
    const divisionMap = new Map<string, string>();

    // Add from divisions prop (authoritative source)
    divisions?.forEach((division) => {
      divisionMap.set(division.id, division.name);
    });

    // Add any missing divisions from forecast data as fallback
    if (forecastData) {
      const forecastDivisionMap = createDivisionIdToNameMap(forecastData);
      forecastDivisionMap.forEach((name, id) => {
        if (!divisionMap.has(id)) {
          divisionMap.set(id, name);
        }
      });
    }

    return divisionMap;
  }, [divisions, forecastData]);

  // Get available tag names for the filter UI
  const { data: rebateRuleTagsEnum } = useCoreEnums({
    name: 'Rebate Rule Tags',
    includeOptions: true,
  });
  const availableTags = useMemo(() => {
    if (!rebateRuleTagsEnum?.edges[0]?.options) return [];
    return rebateRuleTagsEnum.edges[0].options;
  }, [rebateRuleTagsEnum?.edges]);

  // Use rebate filters hook to manage all filter state
  // When controlled from parent, we still run the hook but override with props
  const rebateFiltersResult = useRebateFilters({
    rules: forecastData?.rules,
    rebateRules,
    divisions,
    availableTags,
  });

  // Use controlled props or local state
  const filterFns = filterFnsProp ?? rebateFiltersResult.filterFns;
  const filterGroups = rebateFiltersResult.filterGroups;
  const activeFilterCount = rebateFiltersResult.activeFilterCount;
  const activeFilterNames =
    activeFilterNamesProp ?? rebateFiltersResult.activeFilterNames;
  const clearAllFilters = rebateFiltersResult.clearAllFilters;
  const selectedTagIds = rebateFiltersResult.selectedTagIds;
  const selectedRuleIds = rebateFiltersResult.selectedRuleIds;
  const selectedDivisionIds = rebateFiltersResult.selectedDivisionIds;

  // Calculate current date once - can be mocked for testing/Storybook
  // Use prop if provided, otherwise default to actual current date
  const currentDate = useMemo(() => {
    return currentDateProp || new Date();
  }, [currentDateProp]);

  // Filter forecast data - backend provides both incremental and cumulative values
  // We just select which field to use based on isCumulative flag in display components
  const filteredForecastData = useFilteredForecastData({
    forecastData: forecastData || undefined,
    filterFns,
  });

  // Extend reported line with future points from linear-regression predictions
  // This happens after filtering but before cumulative transformation
  const extendedResult = useExtendedReportedForecast(filteredForecastData);
  const extendedForecastData = extendedResult.forecastData ?? undefined;

  // Calculate split point for past/future predictions (needs filtered/extended data)
  const { lastRealEstimate, predictionSplitDay } = useSplitPoint(
    extendedForecastData,
    currentDate,
  );

  // Use extended data directly - cumulative/incremental selection happens in display components
  const transformedForecastData = extendedForecastData;

  // Create MultiYearForecastData structure (StatusTab doesn't have previous year data)
  const multiYearForecastData: MultiYearForecastData = useMemo(() => {
    const currentYear =
      transformedForecastData?.forecastPeriod?.startDate?.getUTCFullYear() ||
      new Date().getUTCFullYear();
    return {
      currentYear,
      currentYearData: transformedForecastData ?? null,
      previousYearData: null, // StatusTab doesn't show previous year comparisons
      isCumulative,
    };
  }, [transformedForecastData, isCumulative]);

  // Create supplier ID to name map (empty since we don't have suppliers in this context)
  const supplierIdToName = useMemo(() => new Map<string, string>(), []);

  // Find redistribution tag ID (case-insensitive) - compute before useState so initializers can use it
  const redistributionTagId = useMemo(
    () => findRedistributionTagId(tagNameToIdMap),
    [tagNameToIdMap],
  );

  const hasRedistributionTag = redistributionTagId !== null;

  // Create table configs for the breakdown tables
  // These are 4-dimensional tables: TIME × SECONDARY_X (columns) × PRIMARY_Y (rows) × SECONDARY_Y (sub-rows)
  // Use state so time filter can be updated (but dimensions remain fixed)
  // Secondary X dimension is RULE_TAG (with Redistribution grouping) if tag exists, otherwise RULE_TYPE
  const [ruleBreakdownConfig, setRuleBreakdownConfig] = useState<TableConfig>(
    () => {
      const secondaryXDimension = hasRedistributionTag
        ? DimensionType.RULE_TAG
        : DimensionType.RULE_TYPE;

      const dimensionGroupingConfig =
        hasRedistributionTag && redistributionTagId
          ? {
              [DimensionType.RULE_TAG]:
                createRedistributionGroupingConfig(redistributionTagId),
            }
          : undefined;

      return {
        id: 'rule-breakdown',
        primaryXDimension: DimensionType.TIME, // Columns: months
        secondaryXDimension, // Sub-columns: rule tag (Redistribution) or rule type
        primaryYDimension: DimensionType.RULE, // Rows: rules
        secondaryYDimension: DimensionType.DIVISION, // Sub-rows: divisions (creates 4D structure)
        label: 'Rule x Division Breakdown',
        timeFilterPreset: 'all',
        dimensionGroupingConfig,
      };
    },
  );

  const [divisionBreakdownConfig, setDivisionBreakdownConfig] =
    useState<TableConfig>(() => {
      const secondaryXDimension = hasRedistributionTag
        ? DimensionType.RULE_TAG
        : DimensionType.RULE_TYPE;

      const dimensionGroupingConfig =
        hasRedistributionTag && redistributionTagId
          ? {
              [DimensionType.RULE_TAG]:
                createRedistributionGroupingConfig(redistributionTagId),
            }
          : undefined;

      return {
        id: 'division-breakdown',
        primaryXDimension: DimensionType.TIME, // Columns: months
        secondaryXDimension, // Sub-columns: rule tag (Redistribution) or rule type
        primaryYDimension: DimensionType.DIVISION, // Rows: divisions
        secondaryYDimension: DimensionType.RULE, // Sub-rows: rules (creates 4D structure)
        label: 'Division x Rule Breakdown',
        timeFilterPreset: 'all',
        dimensionGroupingConfig,
      };
    });

  // Update handlers that only allow time filter changes (preserve dimensions)
  // These handlers ignore dimension changes - only timeFilterPreset can be updated
  const handleRuleBreakdownUpdate = useCallback(
    (updatedConfig: Omit<TableConfig, 'id' | 'label'>) => {
      setRuleBreakdownConfig((prev) => ({
        ...prev,
        // Explicitly preserve dimensions - ignore any dimension changes from updatedConfig
        // Only update timeFilterPreset (and related fields that don't affect structure)
        timeFilterPreset:
          updatedConfig.timeFilterPreset ?? prev.timeFilterPreset,
        customColumns: updatedConfig.customColumns,
        dimensionGroupingConfig: updatedConfig.dimensionGroupingConfig,
        // Dimensions are intentionally NOT updated - they remain fixed
      }));
    },
    [],
  );

  const handleDivisionBreakdownUpdate = useCallback(
    (updatedConfig: Omit<TableConfig, 'id' | 'label'>) => {
      setDivisionBreakdownConfig((prev) => ({
        ...prev,
        // Explicitly preserve dimensions - ignore any dimension changes from updatedConfig
        // Only update timeFilterPreset (and related fields that don't affect structure)
        timeFilterPreset:
          updatedConfig.timeFilterPreset ?? prev.timeFilterPreset,
        customColumns: updatedConfig.customColumns,
        dimensionGroupingConfig: updatedConfig.dimensionGroupingConfig,
        // Dimensions are intentionally NOT updated - they remain fixed
      }));
    },
    [],
  );

  const handleViewAudit = () => {
    const today = new Date();
    const auditPath = paths.authenticated.rebates.calculationAudit.replace(
      '$agreementId',
      agreementId,
    );
    const searchParams = new URLSearchParams({
      date: today.toISOString().split('T')[0],
      calculationType: 'estimate',
    });
    const fullUrl = `${auditPath}?${searchParams.toString()}`;
    window.open(fullUrl, '_blank');
  };

  // Reset all controls to defaults handler
  const handleResetAll = useCallback(() => {
    // Reset cumulative toggle to true
    setIsCumulative(true);
    // Reset visible lines to all available (use individual algorithm names)
    setVisibleLines(
      new Set([
        'estimates',
        ...ALL_PREDICTION_ALGORITHMS,
        'reported',
        'previousYear',
      ]),
    );
    // Reset primary line to sarima
    setPrimaryLine('sarima');
    // Clear all filters
    clearAllFilters();
  }, [clearAllFilters, setIsCumulative, setVisibleLines, setPrimaryLine]);

  // Check if reset is available (anything changed from defaults)
  const isResetAvailable = useMemo(() => {
    // Check if cumulative is not default (default is true)
    if (!isCumulative) return true;
    // Check if visible lines are not all visible (default is all visible)
    const defaultVisibleLines = new Set([
      'estimates',
      ...ALL_PREDICTION_ALGORITHMS,
      'reported',
      'previousYear',
    ]);
    if (
      visibleLines.size !== defaultVisibleLines.size ||
      !Array.from(defaultVisibleLines).every((line) => visibleLines.has(line))
    ) {
      return true;
    }
    // Check if primary line is not default (default is 'sarima')
    if (primaryLine !== 'sarima') return true;
    // Check if any filters are active
    if (activeFilterCount > 0) return true;
    return false;
  }, [isCumulative, visibleLines, primaryLine, activeFilterCount]);

  // Check if all estimates and predictions are 0
  // Use incremental values for this check (cumulative would always be >= 0)
  const hasAllZeroValues = useMemo(() => {
    if (!forecastData) return false;

    // Check if all estimates are 0 (using incremental values)
    const allEstimatesZero =
      forecastData.estimates.length === 0 ||
      forecastData.estimates.every(
        (point) => Math.abs(point.totalRebateAmount) < 0.01,
      );

    // Check if all predictions are 0 (using incremental values)
    const allPredictionsZero =
      Object.keys(forecastData.predictions).length === 0 ||
      Object.values(forecastData.predictions).every((points) =>
        points.every((point) => Math.abs(point.totalRebateAmount) < 0.01),
      );

    const allReportedZero =
      forecastData.reported.length === 0 ||
      forecastData.reported.every(
        (point) => Math.abs(point.totalRebateAmount) < 0.01,
      );

    return allEstimatesZero && allPredictionsZero && allReportedZero;
  }, [forecastData]);

  // Debug: Log forecast tables when forecastData is available
  if (forecastData && !forecastLoading) {
    logForecastDebugTables(forecastData);
  }

  return (
    <StatusTabView
      multiYearForecastData={multiYearForecastData}
      forecastLoading={forecastLoading}
      hasAllZeroValues={hasAllZeroValues}
      divisionIdToName={divisionIdToName}
      supplierIdToName={supplierIdToName}
      rebateRules={rebateRules}
      filterGroups={filterGroups}
      activeFilterCount={activeFilterCount}
      activeFilterNames={activeFilterNames}
      onClearAllFilters={clearAllFilters}
      isCumulative={isCumulative}
      onCumulativeChange={setIsCumulative}
      visibleLines={visibleLines}
      onVisibleLinesChange={setVisibleLines}
      primaryLine={primaryLine}
      onPrimaryLineChange={(line) => setPrimaryLine(line as LineType)}
      ruleBreakdownCollapsed={ruleBreakdownCollapsed}
      onToggleRuleBreakdown={() =>
        setRuleBreakdownCollapsed(!ruleBreakdownCollapsed)
      }
      divisionBreakdownCollapsed={divisionBreakdownCollapsed}
      onToggleDivisionBreakdown={() =>
        setDivisionBreakdownCollapsed(!divisionBreakdownCollapsed)
      }
      ruleBreakdownConfig={ruleBreakdownConfig}
      divisionBreakdownConfig={divisionBreakdownConfig}
      onUpdateRuleBreakdown={handleRuleBreakdownUpdate}
      onUpdateDivisionBreakdown={handleDivisionBreakdownUpdate}
      userIsAdmin={userIsAdmin}
      onViewAudit={handleViewAudit}
      currentDate={currentDate}
      predictionSplitDay={predictionSplitDay}
      lastRealEstimate={
        lastRealEstimate
          ? {
              daysFromStart: lastRealEstimate.daysFromStart,
              totalRebateAmount: lastRealEstimate.totalRebateAmount,
              date: lastRealEstimate.date,
            }
          : null
      }
      ruleTagsMap={ruleTagsMap}
      tagNameToIdMap={tagNameToIdMap}
      onReset={handleResetAll}
      isResetAvailable={isResetAvailable}
    />
  );
};

export default StatusTab;
